import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Button from "components/button/Button";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import FailedRedNotificationIcon from "components/icons/FailedRedNotificationIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import filteringStyle from "components/licenses/filter-license-keys/license-key-filtering.scss";
import style from "components/licenses/license-keys-table.scss";
import { Result } from "components/licenses/SerialNumberKeysTable";
import tableStyle from "components/reports/erasure-reports-table.scss";
import DeleteItemStatusDialog from "components/status-dialog/DeleteItemStatusDialog";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { licenseService } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    selectedCount: number;
    onDeselect: () => void;
    onEnableDisable: (action: "ENABLE" | "DISABLE") => void;
    onDeleteSuccess: () => void;
    onDeleteStart: () => void;
    getSelectedKeys: () => string[];
    getSelectedKeysWithStatus: () => { licenseKey: string; enabled: boolean }[];
    testIdPrefix: "serialNumber" | "bms";
    onDeleteError: () => void;
    result: Result;
    abortControllers: React.MutableRefObject<AbortController[]>;
}

export const LicenseKeyActions = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [errorModalVisible, setErrorModalVisible] = React.useState(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });

    const handleLicenseKeyDeletion = async (licenseKeys: string[]) => {
        setLoading(true);
        props.onDeleteStart();
        const abortController = new AbortController();
        props.abortControllers.current.push(abortController);

        try {
            const introductionText =
                licenseKeys.length === 1
                    ? t("DeleteLicenseKey.deletedMessage", { licenseKey: licenseKeys[0] })
                    : t("DeleteLicenseKey.licenseKeysDeletedSuccessMessage", { totalKeys: licenseKeys.length });

            await licenseService.deleteLicenseKeys(licenseKeys, abortController);

            toast(
                <SuccessIcon
                    successClass={layoutStyle.customToastSuccessIcon}
                    color={theme.contentBackgroundColor}
                    text={introductionText}
                />,
                {
                    closeButton: (closeToastProps) => (
                        <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                    ),
                    className: layoutStyle.customToastSuccessMessage,
                }
            );

            props.onDeleteSuccess();
        } catch {
            if (!abortController.signal.aborted) {
                toast(
                    <div className={filteringStyle.toastErrorContainer}>
                        <FailedRedNotificationIcon
                            backgroundColor={theme.errorBackgroundColor}
                            iconColor={theme.contentBackgroundColor}
                            size={24}
                        />
                        <div className={filteringStyle.toastErrorContent}>
                            <div>{t("DeleteLicenseKey.licenseKeyNotDeletedTitle")}</div>
                            <div>
                                {t("DeleteLicenseKey.refreshPageAfterFailureMessage")}
                                <a href={`mailto:${SUPPORT_EMAIL}`} style={{ marginLeft: "4px" }}>
                                    {t("DeleteLicenseKey.supportMessage")}
                                </a>
                            </div>
                        </div>
                    </div>,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: `${layoutStyle.customToastSuccessMessage} ${layoutStyle.customToastErrorMessage}`,
                    }
                );

                setResult({
                    title: t("DeleteLicenseKey.licenseKeyNotDeletedTitle"),
                    message: t("DeleteLicenseKey.failureMessage"),
                });

                props.onDeleteError();
            }
        } finally {
            setLoading(false);
            setDeleteModalVisible(false);
        }
    };

    const handleDeleteClick = () => {
        const keys = props.getSelectedKeys();
        if (keys.length === 1) {
            usageStatisticsService.sendEvent({
                category: Category.LICENSE_KEYS,
                action: Action.DELETE_SINGLE_LICENSE_KEY,
            });
        } else if (keys.length > 1) {
            usageStatisticsService.sendEvent({
                category: Category.LICENSE_KEYS,
                action: Action.DELETE_LICENSE_KEYS,
            });
        }
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = () => {
        const keys = props.getSelectedKeys();
        handleLicenseKeyDeletion(keys);
    };

    return (
        <>
            <div className={filteringStyle.selectionActions}>
                <div
                    className={tableStyle.selectInformation}
                    data-testid={testIds.common.primaryView.table.selectCountLabel}
                >
                    {t("LicenseKeys.information.usersSelected", { total: props.selectedCount })}
                </div>
                <div className={style.divider} />
                <Button
                    variant="TERTIARY"
                    onClick={props.onDeselect}
                    data-testid={testIds.common.primaryView.table.deselectButton}
                >
                    {t("LicenseKeys.information.deselect")}
                </Button>
                <div className={style.divider} />
                <Button
                    variant="TERTIARY"
                    onClick={() => props.onEnableDisable("ENABLE")}
                    testId={testIds.workArea.license[props.testIdPrefix].table.enableKeysButton}
                    disabled={!props.getSelectedKeysWithStatus().some((key) => !key.enabled)}
                >
                    {t("LicenseKeys.activate")}
                </Button>
                <div className={style.divider} />
                <Button
                    variant="TERTIARY"
                    onClick={() => props.onEnableDisable("DISABLE")}
                    testId={testIds.workArea.license[props.testIdPrefix].table.disableKeysButton}
                    disabled={!props.getSelectedKeysWithStatus().some((key) => key.enabled)}
                >
                    {t("LicenseKeys.deactivate")}
                </Button>
                <div className={style.divider} />
                <Button
                    variant="TERTIARY"
                    icon="DELETE"
                    onClick={handleDeleteClick}
                    testId={testIds.workArea.license[props.testIdPrefix].table.deleteKeysButton}
                >
                    {t("LicenseKeys.DeleteLicenseKey")}
                </Button>
            </div>

            <DeleteConfirmationDialog
                title={t("DeleteLicenseKey.title")}
                introductionMessage={t("DeleteLicenseKey.deleteMultipleLicenseKeys", {
                    totalKeys: props.getSelectedKeys().length,
                })}
                modalDisplayed={deleteModalVisible}
                setModalDisplayed={setDeleteModalVisible}
                handleCommand={handleDeleteConfirm}
                loading={loading}
                confirmationMessage={t("DeleteLicenseKey.confirmationMessage")}
            />

            <DeleteItemStatusDialog
                title={result.title}
                modalDisplayed={errorModalVisible}
                setModalDisplayed={() => setErrorModalVisible(false)}
                handleCommand={handleDeleteConfirm}
                ditchLink={false}
            />
        </>
    );
};
